import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Filter, Download, RefreshCw, ArrowUpRight, ArrowDownRight, Building2, Upload, MoreHorizontal, Edit, Trash2, Loader2 } from 'lucide-react';
import { getAccountVendors, deleteAccountVendor } from '../core/_requests';
import { useAuth } from '../../auth';
import { ACCOUNTS_PERMS } from '../core/permissions';
import { AccountsActionDropdown, DropdownPosition, getDropdownPosition } from './AccountsActionDropdown';
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext';

export default function AccountsPurchaseVendors() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { state: globalState, vessels: globalVessels } = useGlobalFilter();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const filters = ['All', 'Active', 'Inactive'];

  const [vendors, setVendors] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition | null>(null);

  const fetchVendors = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (globalState.company) {
        params.append('companyId', globalState.company.id.toString());
      }
      if (globalState.vessel) {
        params.append('vesselId', globalState.vessel.id.toString());
      } else if (globalState.company && globalVessels.length > 0) {
        globalVessels.forEach((v: any) => params.append('vesselId', v.id.toString()));
      }
      let data = await getAccountVendors(params);
      if (!Array.isArray(data)) data = [];
      
      // Client-side fallback filtering
      if (globalState.vessel) {
        data = data.filter((v: any) => !v.vesselId || String(v.vesselId) === String(globalState.vessel!.id));
      } else if (globalState.company && globalVessels.length > 0) {
        const vIds = new Set(globalVessels.map((v: any) => String(v.id)));
        data = data.filter((v: any) => !v.vesselId || vIds.has(String(v.vesselId)));
      }
      
      setVendors(data);
    } catch (err) {
      console.error("Failed to fetch vendors", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Stable key derived from vessel IDs — only changes when actual vessel list changes,
  // not on every context re-render (which would cause duplicate requests).
  const globalVesselIds = useMemo(
    () => globalVessels.map((v: any) => v.id).sort().join(','),
    [globalVessels]
  );

  useEffect(() => {
    fetchVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalState.company?.id, globalState.vessel?.id, globalVesselIds]);

  useEffect(() => {
    const closeDropdowns = () => {
      setActiveDropdown(null);
      setDropdownPos(null);
    };
    document.addEventListener('click', closeDropdowns);
    return () => document.removeEventListener('click', closeDropdowns);
  }, []);


  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this vendor?")) return;
    try {
      await deleteAccountVendor(id);
      fetchVendors();
    } catch (err) {
      console.error("Failed to delete", err);
      alert("Failed to delete vendor");
    }
  };

  const filteredVendors = vendors.filter(v => {
    if (activeFilter === 'Active' && v.active === false) return false;
    if (activeFilter === 'Inactive' && v.active !== false) return false;
    
    if (searchQuery) {
      const sq = searchQuery.toLowerCase();
      if (!v.displayName?.toLowerCase().includes(sq) && 
          !v.companyName?.toLowerCase().includes(sq) &&
          !v.email?.toLowerCase().includes(sq)) {
        return false;
      }
    }
    return true;
  });

  const safeNumber = (val: any): number => {
    let num = Number(val);
    if (isNaN(num)) {
      const clean = typeof val === 'string' ? val.replace(/[^0-9.-]/g, '') : '';
      num = Number(clean);
      if (isNaN(num)) num = 0;
    }
    return num;
  };

  const handleDownloadCSV = () => {
    if (filteredVendors.length === 0) {
      alert("No vendor data to export.");
      return;
    }
    const headers = ["Sr. No.", "Name", "Company", "Email", "Phone", "Payables", "Status"];
    const rows = filteredVendors.map((vendor, index) => [
      index + 1,
      vendor.displayName || '-',
      vendor.companyName || '-',
      vendor.email || '-',
      vendor.mobile || vendor.workPhone ? `\t${vendor.mobile || vendor.workPhone}` : '-',
      formatCurrency(vendor.payables || 0),
      vendor.active !== false ? 'Active' : 'Inactive'
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `vendors_export_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalVendors = vendors.length;
  const activeVendors = vendors.filter(v => v.active !== false).length;
  const totalPayables = vendors.reduce((sum, v) => sum + safeNumber(v.payables), 0);
  const totalOverdue = vendors.reduce((sum, v) => sum + safeNumber(v.overdue), 0);

  const formatCurrency = (val: any) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(safeNumber(val));
  };

  const stats = [
    { label: 'Total Vendors', value: totalVendors.toString(), change: '+0%', trend: 'up', color: 'blue' },
    { label: 'Active Vendors', value: activeVendors.toString(), change: '+0%', trend: 'up', color: 'emerald' },
    { label: 'Payables', value: formatCurrency(totalPayables), change: '0%', trend: 'up', color: 'violet' },
    { label: 'Overdue', value: formatCurrency(totalOverdue), change: '0%', trend: 'down', color: 'amber' },
  ];

  const vendor = activeDropdown !== null
    ? filteredVendors.find((vendor) => vendor.id === activeDropdown)
    : null;

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-slate-50">
        
        <div id="sidebar-overlay" className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"></div>
        <main className="flex-1 flex flex-col h-full overflow-hidden relative">
          
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 space-y-5" id="main-scroll">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Accounts / Purchase</div>
                <h1 className="text-xl font-bold text-slate-900 leading-tight">Vendors</h1>
                <p className="text-xs text-slate-500">Manage your vendor relationships and payables</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 text-[11px] text-slate-500">
                  <span>Data refreshed just now</span>
                  <button onClick={fetchVendors} className="p-1 rounded hover:bg-slate-100 transition-colors">
                    <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin text-blue-500' : ''}`} />
                  </button>
                </div>
                {hasPermission(ACCOUNTS_PERMS.VENDOR_CREATE) && (
                  <button onClick={() => navigate('/accounts/purchase/vendors/new')} className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2">
                    <Plus className="w-4 h-4" />New Vendor
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {stats.map((stat, i) => (
                <div key={i} className="card-hover bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-9 h-9 rounded-xl bg-${stat.color}-50 flex items-center justify-center`}>
                      <Building2 className={`w-4.5 h-4.5 text-${stat.color}-600`} />
                    </div>
                    <span className={`text-[10px] font-semibold flex items-center gap-0.5 ${stat.trend === 'up' ? 'text-emerald-600' : 'text-red-500'}`}>
                      {stat.trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}{stat.change}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
                  {filters.map(f => (
                    <button key={f} onClick={() => setActiveFilter(f)} className={`px-3 py-1.5 text-[11px] font-bold rounded-lg whitespace-nowrap transition-all ${activeFilter === f ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
                      {f}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type="text" placeholder="Search vendors..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 w-56 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all" />
                  </div>
                  {/* Filter button kept for future use.
                  <button className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"><Filter className="w-4 h-4 text-slate-500" /></button>
                  */}
                  <button onClick={handleDownloadCSV} className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"><Download className="w-4 h-4 text-slate-500" /></button>
                </div>
              </div>

              <div className="overflow-x-auto pb-24 min-h-[300px]">
                <table className="w-full text-left min-w-175">
                  <thead>
                    <tr className="text-[10px] text-slate-400 uppercase tracking-wider bg-slate-50/50">
                      <th className="px-4 py-3 font-medium text-center text-slate-400">Sr. No.</th>
                      <th className="px-4 py-3 font-medium">Name</th>
                      <th className="px-4 py-3 font-medium">Company</th>
                      <th className="px-4 py-3 font-medium">Email</th>
                      <th className="px-4 py-3 font-medium">Phone</th>
                      <th className="px-4 py-3 font-medium">Payables</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {isLoading ? (
                      <tr>
                        <td colSpan={8} className="p-16 text-center">
                          <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
                          <p className="text-slate-500 font-medium">Loading vendors...</p>
                        </td>
                      </tr>
                    ) : filteredVendors.length === 0 ? (
                      <tr>
                        <td colSpan={8}>
                          <div className="p-16 flex flex-col items-center justify-center">
                            <div className="w-20 h-20 bg-linear-to-br from-blue-50 to-blue-100 text-blue-500 rounded-2xl flex items-center justify-center mb-5"><Building2 className="w-10 h-10" strokeWidth={1.5} /></div>
                            <h3 className="text-lg font-bold text-slate-900 mb-2">{searchQuery ? 'No matching vendors' : 'No vendors found'}</h3>
                            <p className="text-slate-500 text-sm text-center max-w-sm mb-6">Add your vendors to manage purchase orders, bills, and payments efficiently.</p>
                            {!searchQuery && (
                              <div className="flex items-center gap-3">
                                {hasPermission(ACCOUNTS_PERMS.VENDOR_CREATE) && (
                                  <button onClick={() => navigate('/accounts/purchase/vendors/new')} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2"><Plus className="w-4 h-4" />Create Vendor</button>
                                )}
                                <button className="px-5 py-2.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl text-sm font-bold transition-all flex items-center gap-2"><Upload className="w-4 h-4" />Import CSV</button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredVendors.map((vendor, index) => (
                        <tr key={vendor.id} className="hover:bg-slate-50 transition-colors group cursor-pointer" onClick={() => hasPermission(ACCOUNTS_PERMS.VENDOR_UPDATE) && navigate(`/accounts/purchase/vendors/${vendor.id}`)}>
                          <td className="px-4 py-3 text-slate-500 font-medium text-center" onClick={e => e.stopPropagation()}>
                            {index + 1}
                          </td>
                          <td className="px-4 py-3 font-semibold text-blue-600 hover:underline">
                            {vendor.displayName || '-'}
                          </td>
                          <td className="px-4 py-3 text-slate-700 font-medium">
                            {vendor.companyName || '-'}
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {vendor.email || '-'}
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {vendor.mobile || vendor.workPhone || '-'}
                          </td>
                          <td className="px-4 py-3 font-semibold text-slate-700">
                            {formatCurrency(vendor.payables)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${vendor.active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
                              {vendor.active !== false ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right relative">
                            <button 
                              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all focus:opacity-100" 
                              onClick={e => {
                                e.stopPropagation();
                                if (activeDropdown === vendor.id) {
                                  setActiveDropdown(null);
                                  setDropdownPos(null);
                                } else {
                                  setDropdownPos(getDropdownPosition(e.currentTarget as HTMLElement));
                                  setActiveDropdown(vendor.id);
                                }
                              }}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </button>
                            
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            
          </div>
        </main>
      </div>

      {vendor && (
        <AccountsActionDropdown open={true} position={dropdownPos}>
                                {hasPermission(ACCOUNTS_PERMS.VENDOR_UPDATE) && (
                                  <button 
                                    className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                                    onClick={(e) => { e.stopPropagation(); navigate(`/accounts/purchase/vendors/${vendor.id}`); }}
                                  >
                                    <Edit className="w-4 h-4 text-slate-400" /> Edit Vendor
                                  </button>
                                )}
                                {hasPermission(ACCOUNTS_PERMS.VENDOR_DELETE) && (
                                  <>
                                    <div className="h-px bg-slate-100 my-1"></div>
                                    <button 
                                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                      onClick={(e) => handleDelete(e, vendor.id)}
                                    >
                                      <Trash2 className="w-4 h-4 text-red-400" /> Delete Vendor
                                    </button>
                                  </>
                                )}
        </AccountsActionDropdown>
      )}

    </>
  );
}
